<template>
  <div class="reply-pending-list">
    <BobProPage
      ref="bobProPageRef"
      :columns="tableColumns"
      :tableConfig="tableConfig"
      :pageConfig="pageConfig"
      @refresh="onRefresh"
    >
      <template #before-table>
        <ReplyPendingToolbar
          :selection="{
            selectedCount: selection.selectedCount.value,
            isAllSelected: selection.isAllSelected.value,
            isIndeterminate: selection.isIndeterminate.value,
          }"
          :disabled="loading.batch"
          :loading="loading.batch"
          @toggle-all="onToggleAll"
          @select-scope="onSelectScope"
          @trigger="onAction"
        />
      </template>
    </BobProPage>

    <!-- 確認彈窗 -->
    <WarningDialog
      v-if="dialog.visible"
      :confirmBtnLoading="loading.batch"
      @confirm="handleConfirm"
      @close="handleCloseDialog"
    >
      <template #body>
        <div v-html="dialog.content" />
      </template>
    </WarningDialog>
  </div>
</template>

<script>
import { defineComponent, reactive, computed } from 'vue'
import BobProPage from '@/components/BobProPage.vue'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import ReplyPendingToolbar from './components/ReplyPendingToolbar.vue'
import { useTableBatchSelection } from '@/use/useTableBatchSelection'
import { useShop } from '@/use/shop'
import { useBobProPage } from '@/use/useBobProPage'

import {
  GetAppointmentReplyPending,
  GetAppointmentReplyPendingCount,
  BatchMarkAsReplied,
  BatchCancel,
} from '@/api/appointmentReplyPending'
import { formatDate } from '@/utils/date'
import { replyStatusConfig, orderStatusConfig } from '@/config/reservation'
import { i18n } from '@/plugins/i18n/i18n'
import { useRouter } from 'vue-router/composables'

export default defineComponent({
  name: 'ReplyPendingList',
  components: {
    BobProPage,
    WarningDialog,
    ReplyPendingToolbar,
  },
  setup () {
    const { shopId } = useShop()
    const { bobProPageRef } = useBobProPage()
    const router = useRouter()

    // 批次選取 hook 初始化
    const selection = useTableBatchSelection({
      getKey: (row) => row.id,
      getCurrentPageRows: () => bobProPageRef.value?.tableData || [],
      tableData: () => bobProPageRef.value?.tableData, // 自動監聽換頁
    })

    // 載入狀態
    const loading = reactive({
      batch: false,
    })

    // 確認彈窗狀態
    const dialog = reactive({
      visible: false,
      action: 'mark-replied',
      payloadIds: [],
      title: '',
      content: '',
    })

    // 頁面配置
    const pageConfig = computed(() => ({
      title: '預約回覆待確認列表',
      actions: [],
    }))

    // 表格欄位定義
    const tableColumns = computed(() => [
      {
        prop: 'selection',
        label: '',
        width: 84,
        fixed: 'left',
        component: () => import('@/components/BaseElCheckbox.vue'),
        render: () => '',
        props: (row) => ({
          value: selection.isRowSelected(row).value,
          label: row.id,
        }),
        on: (row) => ({
          change: () => {
            selection.toggleRow(row)
          },
        }),
      },
      {
        prop: 'code',
        label: '訂單編號',
        component: 'span',
        fixed: 'left',
        render: (row) => row.code || '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'userName',
        label: '姓名',
        component: 'BaseElButton',
        fixed: 'left',
        props: () => ({
          type: 'text',
        }),
        on: (row) => ({
          click: () => {
            const memberId = row.MemberId
            if (!memberId) return
            router.push({
              name: 'MemberProfile',
              params: { id: memberId },
              query: { tab: 'info' },
            })
          },
        }),
        render: (row) => row.userName || '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'userPhone',
        label: '電話號碼',
        component: 'span',
        render: (row) => row.userPhone || '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'startDate',
        label: '預約日期',
        component: 'span',
        render: (row) => row.start ? formatDate(row.start, 'YYYY/MM/DD') : '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'startTime',
        label: '預約時間',
        component: 'span',
        render: (row) => row.start ? formatDate(row.start, 'HH:mm') : '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'createdAt',
        label: '建立時間',
        component: 'span',
        render: (row) => row.createdAt ? formatDate(row.createdAt, 'YYYY/MM/DD HH:mm') : '-',
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'replyStatus',
        label: '訂單回覆狀態',
        component: () => import('@/components/Tag/Tag.vue'),
        render: (row) => i18n.t(replyStatusConfig[row.replyStatus]?.name) || row.replyStatus,
        props: (row) => ({
          type: replyStatusConfig[row.replyStatus]?.tag || 'info',
        }),
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'status',
        label: '訂單狀態',
        component: () => import('@/components/Tag/Tag.vue'),
        render: (row) => i18n.t(orderStatusConfig[row.status]?.name) || row.status,
        props: (row) => ({
          type: orderStatusConfig[row.status]?.tag || 'info',
        }),
        columnAttrs: {
          minWidth: 120,
        },
      },
      {
        prop: 'operate',
        label: '操作',
        width: 120,
        fixed: 'right',
        component: () => import('@/components/OperateColumn.vue'),
        props: (row) => ({
          items: [
            {
              key: 'mark-replied',
              component: () => import('@/components/BaseElButton.vue'),
              render: () => '已回覆',
              props: { type: 'text', class: ['underline'] },
              on: {
                click: () => openDialog('mark-replied', [row.id], i18n.t(replyStatusConfig[row.replyStatus]?.name)),
              },
            },
            {
              key: 'cancel',
              component: () => import('@/components/BaseElButton.vue'),
              render: () => '取消',
              props: {
                type: 'text',
                class: ['underline', '!text-danger'],
              },
              on: {
                click: () => openDialog('cancel', [row.id], i18n.t(replyStatusConfig[row.replyStatus]?.name)),
              },
            },
          ],
        }),
      },
    ])

    // 表格配置
    const tableConfig = computed(() => ({
      pagination: {
        enabled: true,
        pageLimit: 10,
        pageSizes: [10, 20, 50],
      },
      apis: {
        get: GetAppointmentReplyPending,
        count: GetAppointmentReplyPendingCount,
      },
      filters: [],
      tableAttrs: {
        class: '!rounded-t-none',
      },
    }))

    // 批次事件處理器
    const onToggleAll = (checked) => {
      selection.toggleAll(checked)
    }

    const onSelectScope = (scope) => {
      if (scope === 'clear') {
        return selection.clear()
      }

      if (scope === 'all') {
        return selection.toggleAll(true)
      }

      // 按狀態選取（pending, expired 等）
      return selection.selectByFilter(row => row.replyStatus === scope)
    }

    const onAction = (action) => {
      if (selection.selectedCount.value === 0) {
        return window.$message.warning('請先選取項目')
      }

      const firstRow = selection.selectedRows.value[0]
      const statusLabel = i18n.t(replyStatusConfig[firstRow?.replyStatus]?.name) || '-'

      openDialog(action, selection.selectedIds.value, statusLabel)
    }

    // 開啟確認彈窗
    const openDialog = (action, payloadIds, statusLabel) => {
      dialog.visible = true
      dialog.action = action
      dialog.payloadIds = payloadIds

      // 組裝狀態標籤文字（若有提供）
      const count = payloadIds.length

      // 依據 action 決定標題與內容文字
      if (action === 'mark-replied') {
        dialog.content = `確認將 <span class="text-primary-100">${count} 筆${statusLabel}</span> 訂單調整為 <span class="text-primary-100">已回覆</span>`
      } else if (action === 'cancel') {
        dialog.content = `確認 <span class="text-primary-100">取消 ${count} 筆${statusLabel}</span> 的訂單`
      }
    }

    // 關閉確認彈窗
    const handleCloseDialog = () => {
      dialog.visible = false
      dialog.action = 'mark-replied'
      dialog.payloadIds = []
      dialog.title = ''
      dialog.content = ''
    }

    // 確認操作處理
    const handleConfirm = async () => {
      loading.batch = true
      try {
        const payload = {
          shopId: shopId.value,
          appointmentOrderIds: dialog.payloadIds,
        }

        if (dialog.action === 'mark-replied') {
          await BatchMarkAsReplied(payload)
          window.$message.success('已成功標記為已回覆')
        } else if (dialog.action === 'cancel') {
          await BatchCancel(payload)
          window.$message.success('已成功取消預約訂單')
        }

        // 清空選取並刷新列表
        selection.clear()
        handleCloseDialog()
        onRefresh()
      } catch (error) {
        console.error('[ReplyPendingList] Batch operation failed:', error)
        window.$message.error(error?.message || '操作失敗,請稍後再試')
      } finally {
        loading.batch = false
      }
    }

    // 刷新列表
    const onRefresh = () => {
      bobProPageRef.value?.refresh()
    }

    return {
      bobProPageRef,
      tableColumns,
      tableConfig,
      pageConfig,
      selection,
      loading,
      dialog,
      onToggleAll,
      onSelectScope,
      onAction,
      openDialog,
      handleCloseDialog,
      handleConfirm,
      onRefresh,
    }
  },
})
</script>

<style lang="scss" scoped>
.reply-pending-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
